// pages/GreetingCards.js - User's Greeting Cards List
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiMail, FiEdit2, FiTrash2, FiEye, FiShare2, FiLock, FiGlobe } from 'react-icons/fi';
import DashboardLayout from '../components/DashboardLayout';
import { getUserGreetingCards, deleteGreetingCard } from '../services/greetingCardService';
import { useAuth } from '../services/auth';
import '../styles/GreetingCards.scss';

import { cardUrl } from '../shared/shortLinks';
const GreetingCards = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadCards();
  }, []);

  const loadCards = async () => {
    try {
      setLoading(true);
      const data = await getUserGreetingCards();
      setCards(data);
    } catch (error) {
      console.error('Error loading cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (cardId) => {
    try {
      setDeleting(true);
      await deleteGreetingCard(cardId);
      setCards(cards.filter(card => card.id !== cardId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting card:', error);
      alert('Failed to delete card. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const getCoverImageUrl = (card) => {
    if (card.cover_image_type === 'gif') {
      return card.cover_static_gif_cdn_url || card.cover_image_cdn_url;
    }
    if (card.cover_image_type === 'youtube') {
      return card.youtube_thumbnail_cdn_url || card.cover_thumbnail_cdn_url;
    }
    if (card.cover_image_type === 'pdf' && card.pdf_page_images?.length > 0) {
      return card.pdf_page_images[0].cdn_url;
    }
    return card.cover_image_cdn_url || card.cover_thumbnail_cdn_url;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="greeting-cards-page">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading your cards...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="greeting-cards-page">
        {/* Header */}
        <div className="page-header">
          <div className="header-content">
            <div className="header-icon">
              <FiMail />
            </div>
            <div className="header-text">
              <h1>Greeting Cards</h1>
              <p>Create and share beautiful greeting cards</p>
            </div>
          </div>
          <button
            className="btn btn-primary create-btn"
            onClick={() => navigate('/create-greeting-card')}
          >
            <FiPlus />
            <span>Create Card</span>
          </button>
        </div>

        {/* Cards Grid */}
        {cards.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <FiMail />
            </div>
            <h2>No Greeting Cards Yet</h2>
            <p>Create your first greeting card to share with friends and family</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/create-greeting-card')}
            >
              <FiPlus />
              <span>Create Your First Card</span>
            </button>
          </div>
        ) : (
          <div className="cards-grid">
            {cards.map(card => (
              <motion.div
                key={card.id}
                className="card-item"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                whileHover={{ y: -5 }}
              >
                {/* Card Preview */}
                <div
                  className="card-preview"
                  onClick={() => navigate(`/c/${card.shareable_slug}`)}
                >
                  {getCoverImageUrl(card) ? (
                    <img
                      src={getCoverImageUrl(card)}
                      alt={card.title}
                      className="card-cover"
                    />
                  ) : (
                    <div className="card-placeholder">
                      <FiMail />
                    </div>
                  )}

                  {/* Privacy Badge */}
                  <div className={`privacy-badge ${card.privacy}`}>
                    {card.privacy === 'private' ? (
                      <>
                        <FiLock />
                        <span>Private</span>
                      </>
                    ) : (
                      <>
                        <FiGlobe />
                        <span>Public</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Card Info */}
                <div className="card-info">
                  <h3>{card.title}</h3>
                  {card.notes && (
                    <p className="card-notes">
                      {card.notes.length > 60
                        ? `${card.notes.substring(0, 60)}...`
                        : card.notes}
                    </p>
                  )}
                  <p className="card-date">Created {formatDate(card.created_at)}</p>
                </div>

                {/* Actions */}
                <div className="card-actions">
                  <button
                    className="btn-icon"
                    onClick={() => navigate(`/c/${card.shareable_slug}`)}
                    title="View Card"
                  >
                    <FiEye />
                  </button>
                  <button
                    className="btn-icon"
                    onClick={() => navigate(`/edit-greeting-card/${card.id}`)}
                    title="Edit Card"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    className="btn-icon"
                    onClick={() => {
                      const url = cardUrl(card.shareable_slug, window.location.origin);
                      navigator.clipboard.writeText(url);
                      alert('Link copied to clipboard!');
                    }}
                    title="Share Card"
                  >
                    <FiShare2 />
                  </button>
                  <button
                    className="btn-icon delete"
                    onClick={() => setDeleteConfirm(card.id)}
                    title="Delete Card"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {deleteConfirm && (
            <motion.div
              className="modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !deleting && setDeleteConfirm(null)}
            >
              <motion.div
                className="modal-content"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={e => e.stopPropagation()}
              >
                <h3>Delete Card?</h3>
                <p>Are you sure you want to delete this greeting card? This action cannot be undone.</p>
                <div className="modal-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setDeleteConfirm(null)}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDelete(deleteConfirm)}
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
};

export default GreetingCards;
